import { lazy, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { format } from 'date-fns';
import { Download, Eye, Filter } from 'lucide-react';

import Avatar from 'src/componentes/avatar';
import Button from 'src/componentes/button';
import ContentManager from 'src/componentes/content-manager';
import List from 'src/componentes/list';
import ListColumn from 'src/componentes/list/list-column';
import useAuth from 'src/hooks/useAuth';
import formatDateFns from 'src/utils/formatDateFns';
import { capitalizeFirstLetter } from 'src/utils/string';
import RespostasFiltroChips from './RespostasFiltroChips';

const RespostaPreviewModal = lazy(() => import('./RespostaPreviewModal'));

const RespostasList = ({
  ehConfidencial = false,
  entidadeId,
  filtros,
  formularioUsoId,
  isExporting,
  onExportar,
  onOpenFiltro,
  onRemoveFiltro,
  refreshToken,
}) => {
  const intl = useIntl();
  const { terms: resources, user } = useAuth();
  const { currentDisplay, currentLocale } = formatDateFns(user?.idioma, true);

  const parameters = useMemo(
    () => ({
      entidadeId,
      formularioUsoId,
      areas: filtros.areas,
      considerarAreasSubordinadas: filtros.considerarAreasSubordinadas,
      colaboradoresGrupos: ehConfidencial ? null : filtros.colaboradoresGrupos,
      dataEnvioInicio: filtros.dataEnvioInicio,
      dataEnvioFim: filtros.dataEnvioFim,
    }),
    [ehConfidencial, entidadeId, filtros, formularioUsoId]
  );

  const renderDataEnvio = ({ dataEnvio }) =>
    dataEnvio
      ? format(new Date(dataEnvio), currentDisplay, { locale: currentLocale })
      : '-';

  const renderArea = ({ area }) => area?.nome || area || '-';

  const renderColaborador = ({ colaborador }) => {
    if (!colaborador?.nome) return '-';

    return (
      <Avatar
        border={1}
        foto={colaborador.fotoArquivo}
        label={colaborador.nome}
        size={28}
      />
    );
  };

  const renderTexto = (value) => value || '-';

  const handleVisualizarResposta = (item) => {
    if (!item?.id) return;

    ContentManager.addContent(<RespostaPreviewModal respostaId={item.id} />);
  };

  const renderResposta = (item) => (
    <Button
      className="p-0"
      color="link"
      leftIcon={Eye}
      iconSize={18}
      onClick={() => handleVisualizarResposta(item)}
    >
      {intl.formatMessage({ id: 'labelVisualizar' })}
    </Button>
  );

  const toolbarActions = [
    <RespostasFiltroChips
      key="filtros"
      className="mb-0 mr-2"
      ehConfidencial={ehConfidencial}
      filtros={filtros}
      onRemove={onRemoveFiltro}
      style={{ maxWidth: 520 }}
    />,
    <Button
      key="filtrar"
      className="ml-2"
      color="outline-primary"
      leftIcon={Filter}
      onClick={onOpenFiltro}
    >
      {intl.formatMessage({ id: 'label.filtrar' })}
    </Button>,
    <Button
      key="exportar"
      className="ml-2"
      color="outline-primary"
      disabled={isExporting}
      leftIcon={Download}
      onClick={onExportar}
    >
      {intl.formatMessage({ id: 'labelExportarRespostas' })}
    </Button>,
  ];

  return (
    <List
      key={`respostas-${entidadeId}-${formularioUsoId}-${
        ehConfidencial ? 'confidencial' : 'identificada'
      }`}
      url="/FormularioGlobalRespostas"
      parameters={parameters}
      saved={refreshToken}
      actionsToolBarEnd={toolbarActions}
      fillAvailableHeight
      hideColumnsButton
      hideFiltroChip
      ignorePermissions
      openLastFilter={false}
      pageSize={20}
      showSearch
      showToolBar
      showMenu={false}
      showNew={false}
      showEdit={false}
      showDelete={false}
      showExport={false}
      modelValue={{}}
    >
      <ListColumn
        default
        headerText={capitalizeFirstLetter(
          intl.formatMessage({ id: 'dataEnvio' })
        )}
        valueField="dataEnvio"
        sortField="dataEnvio"
        valueFunction={renderDataEnvio}
        minWidth={140}
      />
      <ListColumn
        default
        headerText={capitalizeFirstLetter(resources.area)}
        valueField="area"
        valueFunction={renderArea}
        minWidth={150}
      />
      {!ehConfidencial && (
        <ListColumn
          default
          headerText={capitalizeFirstLetter(resources.colaborador)}
          valueField="colaborador"
          valueFunction={renderColaborador}
          minWidth={180}
        />
      )}
      {!ehConfidencial && (
        <ListColumn
          default
          headerText={capitalizeFirstLetter(
            intl.formatMessage({ id: 'matricula' })
          )}
          valueField="matricula"
          valueFunction={({ colaborador }) =>
            renderTexto(colaborador?.matricula)
          }
          minWidth={120}
        />
      )}
      {!ehConfidencial && (
        <ListColumn
          default
          headerText={capitalizeFirstLetter(
            intl.formatMessage({ id: 'email' })
          )}
          valueField="email"
          valueFunction={({ colaborador }) => renderTexto(colaborador?.email)}
          minWidth={180}
        />
      )}
      <ListColumn
        default
        headerText={capitalizeFirstLetter(
          intl.formatMessage({ id: 'resposta' })
        )}
        valueField="id"
        valueFunction={renderResposta}
        minWidth={140}
      />
    </List>
  );
};

export default RespostasList;
